import {
  selectInvoicePagination,
  setInvoicePage,
  setInvoicePageSize,
} from '@saaga/shared/store/slices/billing-ui.slice'
import { transformInvoiceListResponse } from '@saaga/shared/utils/billing.util'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import type { BillingInvoice, InvoiceStatus } from '@/components/billing/billing.interface'
import type {
  DataTableFilterValue,
  DataTablePagination,
  SortOrder,
} from '@/components/data-table/data-table.interface'
import { useBillingOperations } from '@/hooks/billing/use-billing-operations.hook'

export interface UseInvoicesSettingsReturn {
  invoiceList: BillingInvoice[]
  pagination: DataTablePagination
  isLoading: boolean
  error: string | null
  sortBy: string
  sortOrder: SortOrder
  filterValueList: DataTableFilterValue[]
  handlePageChange: (page: number) => void
  handlePageSizeChange: (pageSize: number) => void
  handleSortChange: (sortBy: string, sortOrder: SortOrder) => void
  handleFilterChange: (filterValueList: DataTableFilterValue[]) => void
  handleDownload: (downloadUrl: string) => void
  handleRetry: () => void
}

const useFetchInvoices = () => {
  const { listInvoices } = useBillingOperations()
  const [invoiceList, setInvoiceList] = useState<BillingInvoice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchInvoices = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await listInvoices(100)
      const transformedList = transformInvoiceListResponse(response.data)
      setInvoiceList(transformedList as BillingInvoice[])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoices')
    } finally {
      setIsLoading(false)
    }
  }, [listInvoices])

  useEffect(() => {
    fetchInvoices()
  }, [fetchInvoices])

  return { invoiceList, isLoading, error, fetchInvoices }
}

const useTableState = () => {
  const dispatch = useDispatch()
  const pagination = useSelector(selectInvoicePagination)
  const [filterValueList, setFilterValueList] = useState<DataTableFilterValue[]>([])
  const [sortBy, setSortBy] = useState<string>('createdAt')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const handlePageChange = useCallback((page: number) => dispatch(setInvoicePage(page)), [dispatch])
  const handlePageSizeChange = useCallback(
    (pageSize: number) => dispatch(setInvoicePageSize(pageSize)),
    [dispatch]
  )
  const handleSortChange = useCallback(
    (newSortBy: string, newSortOrder: SortOrder) => {
      setSortBy(newSortBy)
      setSortOrder(newSortOrder)
      dispatch(setInvoicePage(1))
    },
    [dispatch]
  )
  const handleFilterChange = useCallback(
    (newFilterValueList: DataTableFilterValue[]) => {
      setFilterValueList(newFilterValueList)
      dispatch(setInvoicePage(1))
    },
    [dispatch]
  )

  return {
    pagination,
    filterValueList,
    sortBy,
    sortOrder,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    handleFilterChange,
  }
}

const filterAndSortInvoices = (
  invoiceList: BillingInvoice[],
  filterValueList: DataTableFilterValue[],
  sortBy: string,
  sortOrder: SortOrder
): BillingInvoice[] => {
  let result = [...invoiceList]

  const statusFilter = filterValueList.find((f) => f.filterId === 'status')
  if (statusFilter && statusFilter.valueList.length > 0) {
    result = result.filter((invoice) =>
      statusFilter.valueList.includes(invoice.status as InvoiceStatus)
    )
  }

  if (sortBy === 'createdAt') {
    result.sort((a, b) => {
      const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      return sortOrder === 'asc' ? diff : -diff
    })
  }

  if (sortBy === 'amountDue') {
    result.sort((a, b) =>
      sortOrder === 'asc' ? a.amountDue - b.amountDue : b.amountDue - a.amountDue
    )
  }

  return result
}

export const useInvoicesSettings = (): UseInvoicesSettingsReturn => {
  const { invoiceList, isLoading, error, fetchInvoices } = useFetchInvoices()
  const tableState = useTableState()

  const handleDownload = useCallback((downloadUrl: string) => {
    window.open(downloadUrl, '_blank', 'noopener,noreferrer')
  }, [])

  const filteredData = useMemo(
    () =>
      filterAndSortInvoices(
        invoiceList,
        tableState.filterValueList,
        tableState.sortBy,
        tableState.sortOrder
      ),
    [invoiceList, tableState.filterValueList, tableState.sortBy, tableState.sortOrder]
  )

  const paginatedData = useMemo(() => {
    const start = (tableState.pagination.page - 1) * tableState.pagination.pageSize
    return filteredData.slice(start, start + tableState.pagination.pageSize)
  }, [filteredData, tableState.pagination.page, tableState.pagination.pageSize])

  return {
    invoiceList: paginatedData,
    pagination: {
      page: tableState.pagination.page,
      pageSize: tableState.pagination.pageSize,
      totalCount: filteredData.length,
    },
    isLoading,
    error,
    sortBy: tableState.sortBy,
    sortOrder: tableState.sortOrder,
    filterValueList: tableState.filterValueList,
    handlePageChange: tableState.handlePageChange,
    handlePageSizeChange: tableState.handlePageSizeChange,
    handleSortChange: tableState.handleSortChange,
    handleFilterChange: tableState.handleFilterChange,
    handleDownload,
    handleRetry: fetchInvoices,
  }
}
